import { NotFound } from './objects/NotFound';
import { Index } from './objects/Index';
import { Login } from './objects/Login';
import { useEffect, useState } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';

import './styles/NewIndexPage.css';
import './styles/DefaultPage.css';

import { RegisterVerify } from './objects/RegisterVerify';
import { Results } from './objects/Results';
import { Recover } from './objects/Recover';
import { PostJob } from './objects/PostJob';
import { GetDemo } from './objects/GetDemo';
import { Feedback } from './objects/Feedback';
import { Help } from './objects/Help';
import { Plans } from './objects/Plans';
import { Payment } from './objects/Payment';
import { Profile } from './objects/Profile';
import { AdminDashboard } from './objects/AdminDashboard';
import { BasicFooter } from './objects/BasicFooter';
import { RegisterEmail } from "./objects/RegisterEmail";
import { RegisterComplete } from "./objects/RegisterComplete";
import { SentryPageBoundary } from './objects/SentryPageBoundary';


import { updateColors, useTheme } from './hooks/ThemeContext';

const withBoundary = (boundaryName, element) => (
  <SentryPageBoundary boundaryName={boundaryName}>
    {element}
  </SentryPageBoundary>
);

function App() {
  const { setTheme } = useTheme();
  const [firstTimer, setFirstTimer] = useState(true);

  const location = useLocation();
  const hideFooter = ["/feedback"].includes(location.pathname);

  /* Load saved theme */
  useEffect(() => {
    const savedThemeData = localStorage.getItem("theme-colors");
    if (savedThemeData) {
      updateColors(JSON.parse(savedThemeData), setTheme);
      setFirstTimer(false);
    }
  }, [setTheme]);

  /* Body class switching + page titles */
  useEffect(() => {
    document.body.className = "";
    const path = location.pathname;

    const titles = {
      "/login":           "Login | Rezify",
      "/register":        "Register | Rezify",
      "/register_complete": "Register | Rezify",
      "/verify_email":    "Verify Email | Rezify",
      "/results":         "Results | Rezify",
      "/recover":         "Recover Password | Rezify",
      "/change_password": "Change Password | Rezify",
      "/post_job":        "Post a Job | Rezify",
      "/get_demo":        "Get a Demo | Rezify",
      "/plans":           "Plans | Rezify",
      "/payment":         "Payment | Rezify",
      "/profile":         "Profile | Rezify",
      "/admin":           "Admin | Rezify",
      "/feedback":        "Feedback | Rezify",
      "/help":            "Help | Rezify",
    };

    document.title = titles[path] ?? "Rezify - Your AI-Powered Internship Search";

    if (path === "/" || path === "/index") document.body.classList.add("index");
    else if (path === "/login") document.body.classList.add("login");
    else if (
        ["/register", "/register_complete", "/verify_email", "/recover", "/change_password"].includes(path)
    ) {
        document.body.classList.add("register");
    }
    else if (path === "/results") document.body.classList.add("results");
    else if (path === "/post_job") document.body.classList.add("postjob");
    else if (path === "/get_demo") document.body.classList.add("getdemo");
    else if (path === "/plans") document.body.classList.add("plans");
    else if (path === "/payment") document.body.classList.add("payment");
    else if (path === "/profile") document.body.classList.add("profile");
    else if (path === "/admin") document.body.classList.add("admin");
    else if (path === "/feedback") document.body.classList.add("feedback");
    else if (path === "/help") document.body.classList.add("help");
    else document.body.classList.add("index");
  }, [location.pathname]);

  return (
    <SentryPageBoundary boundaryName="app-shell">
      <div className="app-shell">
        <main className="app-content">
          <Routes>
            <Route path="/" element={withBoundary('index-root', <Index firstTimer={firstTimer} />)} />
            <Route path="/index" element={withBoundary('index', <Index firstTimer={firstTimer} />)} />
            <Route path="/login" element={withBoundary('login', <Login />)} />
            <Route path="/register" element={withBoundary('register-email', <RegisterEmail />)} />
            <Route path="/register_complete" element={withBoundary('register-complete', <RegisterComplete />)} />
            <Route path="/verify_email" element={withBoundary('register-verify', <RegisterVerify />)} />
            <Route path="/results" element={withBoundary('results', <Results />)} />
            <Route path="/recover" element={withBoundary('recover', <Recover recover />)} />
            <Route path="/change_password" element={withBoundary('change-password', <Recover recover={false} />)} />
            <Route path="/post_job" element={withBoundary('post-job', <PostJob />)} />
            <Route path="/get_demo" element={withBoundary('get-demo', <GetDemo />)} />
            <Route path="/:school" element={withBoundary('school-index', <Index firstTimer={firstTimer} />)} />
            <Route path="/plans" element={withBoundary('plans', <Plans />)} />
            <Route path="/payment" element={withBoundary('payment', <Payment />)} />
            <Route path="/profile" element={withBoundary('profile', <Profile />)} />
            <Route path="/admin" element={withBoundary('admin-dashboard', <AdminDashboard />)} />
            <Route path="/feedback" element={withBoundary('feedback', <Feedback />)} />
            <Route path="/help" element={withBoundary('help', <Help />)} />
            <Route path="*" element={withBoundary('not-found', <NotFound />)} />
          </Routes>
        </main>

        {!hideFooter && withBoundary('basic-footer', <BasicFooter />)}
      </div>
    </SentryPageBoundary>
  );
}

export default App;
